<script setup>
import {onMounted, reactive, ref} from "vue";
import axios from "axios";
import url from "@/config/urls";
import router from "@/router/index.js";
import Password from "@parts/Password.vue"

const customerUnityCode = ref('');
const password = reactive({
  current: '',
  new: '',
  confirm: ''
});
const errorMessage = reactive({
  current: '',
  new: '',
  confirm: ''
});

const mapResponseError = (response, fieldList) => {
  return response.data.error
  .map(errorObject => fieldList.includes(errorObject.field) ? errorObject.message : null)
  .filter(item => item !== null)
  .join('\n');
}

// 更新ボタン押下時
const onClickUpdateButton = () => {
  // バリデーションチェック
  if (!password.current) {
    errorMessage.current = '現在のパスワードを入力してください。';
  } else {
    errorMessage.current = '';
  }

  if (!password.new) {
    errorMessage.new = '新しいパスワードを入力してください。';
  } else if (password.new.length < 8) {
    errorMessage.new = '新しいパスワードは8文字以上で入力してください。';
  } else if (password.new.length > 255) {
    errorMessage.new = '新しいパスワードは255文字以内で入力してください。';
  } else if (
      !password.new.match(/^[a-zA-Z0-9^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]{8,255}$/)) {
    errorMessage.new = 'パスワードに使用できない文字が含まれています。';
  } else if (
      !password.new.match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).*$/)) {
    errorMessage.new = '英字大文字、英字小文字、数字、いずれも含むパスワードを設定してください。';
  } else if (password.current === password.new) {
    errorMessage.new = '新しいパスワードには現在のパスワードとは異なるパスワードを設定してください。';
  } else {
    errorMessage.new = '';
  }

  if (!password.confirm) {
    errorMessage.confirm = '新しいパスワード(確認用)を入力してください。';
  } else if (password.new !== password.confirm) {
    errorMessage.confirm = '新しいパスワードと新しいパスワード(確認用)に入力された値が一致していません。';
  } else {
    errorMessage.confirm = '';
  }

  if (errorMessage.current || errorMessage.new || errorMessage.confirm) {
    return;
  }

  // パスワード更新APIの呼び出し
  axios.post(props.updatePasswordUrl, {
    currentPassword: password.current,
    newPassword: password.new
  })
  .then(response => {
    if (response.data.response.updated) {
      sessionStorage.setItem('flushMessage', 'パスワードを更新しました。');
      router.push(props.settingPageUrl);
    } else {
      errorMessage.current =
          response.data.error.map(errorObject => errorObject.message).join('\n');
    }
  })
  .catch(error => {
    const response = error?.response;
    switch (response?.status) {
      case 400:
        if (response.data.error instanceof Array) {
          errorMessage.current = mapResponseError(response, ['currentPassword']);
          errorMessage.new = mapResponseError(response, ['newPassword']);
        } else {
          sessionStorage.setItem('errorHeading', '指定されたページを表示できません。');
          sessionStorage.setItem('errorBody', '申し訳ございませんが、リクエストを正しく処理できませんでした。送信内容が正確であることを確認し、もう一度お試しください。');
          router.push(props.error);
        }
        break;
      case 401:
        router.replace(props.loginUrl);
        break;
      default:
        router.replace(props.error);
        break;
    }
  });
}

// 戻るボタン押下時
const onClickBackButton = () => router.push(props.settingPageUrl);

// コンポーネントマウント後の処理
onMounted(() => {
  // ユーザー情報取得APIの呼び出し
  axios.get(props.userDataUrl)
  .then(response => customerUnityCode.value = response.data.response.customerUnityCode)
  .catch(error => error?.response?.status === 401 ? router.replace(props.loginUrl) : router.replace(props.error));
});

// コンポーネントに渡されたpropsの取得
const props = defineProps({
  isAuthenticated: Boolean,
  userDataUrl: {type: String, default: url.api.userData},
  updatePasswordUrl: {type: String, default: url.api.password},
  settingPageUrl: {type: String, default: url.page.setting.index},
  loginUrl: {type: String, default: url.login},
  error: {type: String, default: url.error}
});
</script>

<template>
  <div id="contents1">
    <div class="com_inner2 clearfix">
      <h2 class="com_title1 b">ユーザー管理パスワード変更</h2>
      <div class="com_rbg1">
        <div class="com_inner3">
          <form class="form-group">
            <label for="username">ご利用ID</label>
            <input id="username" type="text" :value="customerUnityCode" autocomplete="username"
                   readonly class="form-control com_form1 s1">
            <p class="com_color2 com_fs16s com_mgb20s com_mgt30">
              新しいパスワードをご入力いただき、<br>パスワードを再設定してください。
            </p>
            <input type="password" autocomplete="current-password" tabindex="-1" aria-hidden="true"
              style="
                position: absolute;
                left: -9999px;
                width: 1px;
                height: 1px;
                opacity: 0;
              "
            />
            <Password id="current-password" v-model="password.current"
                      placeholder="現在のパスワード" :error="errorMessage.current !== ''"/>
            <div v-if="errorMessage.current" class="com_validate_msg1 error">
              {{ errorMessage.current }}
            </div>
            <div class="com_mgt15 com_mgt10s">
              <Password id="new-password" v-model="password.new" placeholder="新しいパスワード"
                        autocomplete="new-password" :error="errorMessage.new !== ''"/>
            </div>
            <div v-if="errorMessage.new" class="com_validate_msg1 error">
              {{ errorMessage.new }}
            </div>
            <div class="com_mgt15 com_mgt10s">
              <Password id="confirm-password" v-model="password.confirm"
                        placeholder="新しいパスワード(確認用)" autocomplete="new-password"
                        :error="errorMessage.confirm !== ''"/>
            </div>
            <div v-if="errorMessage.confirm" class="com_validate_msg1 error">
              {{ errorMessage.confirm }}
            </div>
          </form>
          <div class="com_btn_group1">
            <button type="button" class="btn com_btn3 cl1" @click="onClickUpdateButton">
              更新
            </button>
            <button type="button" class="btn com_btn3" @click="onClickBackButton">
              戻る
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.com_validate_msg1.error {
  white-space: pre-wrap;
}
</style>